import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../ui/toast';
import { useGetCCAConfigQuery } from '../../../../../utils/api_service/ccaApi';
import {
  applyJob,
  getJobPost,
} from '../../../../../utils/api_service/JobPosting';
import {
  useGetPreQualVideoPresignMutation,
  useLazyGetPreQualConfigQuery,
  useLazyGetPreQualFormQuery,
  useSubmitPublicPreQualMutation,
} from '../../../../../utils/api_service/preQualApi';
import { getVideoFromStorage } from '../../../../../utils/api_service/storage_api';
import { RootState } from '../../../../../utils/redux_store/store';
import {
  PreScreenAnswers,
  PreScreenQuestion,
} from '../../../../../utils/types/assessment_types';
import { JobPostingData } from '../../../../../utils/types/JobOpenings';
import { DIALOG_TYPE } from '../../../Constants/DialogTypes';
import { setAppLocale, SUPPORTED_LOCALES, SupportedLocale } from '../../../../../i18n/locale';

export const useJobPostDetails = () => {
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState<string>('');
  const [jobPostData, setJobPostData] = useState<JobPostingData>();
  const [contentLang, setContentLang] = useState<string>('en');
  const { i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<{
    [type: string]: File[];
  }>({});
  const [jobId, setJobId] = useState<string>('');
  const [jobPostId, setJobPostId] = useState<string>('');
  const [jobPostUrl, setJobPostUrl] = useState<string>('');
  const [url, setUrl] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const [isQualifyingModalOpen, setIsQualifyingModalOpen] = useState(false);
  const [isQualifyingCancelDialogOpen, setIsQualifyingCancelDialogOpen] =
    useState(false);
  const [currentQualifyingStep, setCurrentQualifyingStep] = useState(0);
  const [qualifyingAnswers, setQualifyingAnswers] = useState<
    PreScreenAnswers[]
  >([]);
  const [jobApplicantId, setJobApplicantId] = useState<string | null>(null);

  const [submitPublicPreQual] = useSubmitPublicPreQualMutation();
  const [getVideoPresign] = useGetPreQualVideoPresignMutation();
  const { data: ccaConfig } = useGetCCAConfigQuery(jobId, { skip: !jobId });
  const ccaEnabled =
    !!ccaConfig &&
    (ccaConfig.enabled === true ||
      (ccaConfig.questions?.length ?? 0) > 0 ||
      (ccaConfig.quickConfig?.subdomains?.length ?? 0) > 0);

  const [triggerPreQual, { data: preQualConfig }] =
    useLazyGetPreQualConfigQuery();
  const [triggerPreQualForm, { data: preQualForm }] =
    useLazyGetPreQualFormQuery();

  const isHttpUrl = (value: string) => {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Initial fetch for questions when jobId is available
  useEffect(() => {
    if (jobId) {
      triggerPreQual(jobId);
    }
  }, [id, jobId, triggerPreQual]);

  const qualifyingQuestions: PreScreenQuestion[] = React.useMemo(() => {
    const rawQuestions =
      preQualForm?.questions || preQualConfig?.questions || [];
    if (!rawQuestions) return [];

    return rawQuestions.map((q: any, index: number) => ({
      questionId: q.questionKey || `temp-key-${index}-${Date.now()}`,
      question: q.question,
      questionType:
        q.questionType === 'number'
          ? 'numeric'
          : q.questionType === 'video-answer'
            ? 'video'
            : q.questionType,
      choices: q.choices,
    }));
  }, [preQualConfig, preQualForm]);


  useEffect(() => {
    const fetchJobPost = async () => {
      setLoading(true);
      try {
        // Always pass the selected language so the backend returns the right
        // translation (including ?lang=en to get the English version when the
        // main record's htmlTemplate may have been saved in another language).
        const response = await getJobPost(id, contentLang);
        const responseData = await response.json();

        const jobPosting = responseData.jobPosting || responseData;

        const documentRequirements = (
          responseData.documentRequirements ||
          jobPosting.documentRequirements ||
          []
        ).map((req: any) => ({
          ...req,
          key: req.key,
        }));

        let documentConfigurations =
          responseData.documentConfigurations ||
          jobPosting.documentConfigurations ||
          [];

        if (documentConfigurations.length > 0) {
          documentConfigurations = documentConfigurations.map(
            (config: any) => ({
              ...config,
              type: config.type,
            }),
          );
        }

        const extractedJobOpeningId =
          responseData.jobOpeningId ||
          jobPosting.jobOpeningId ||
          responseData.job_opening_id ||
          jobPosting.job_opening_id;

        setJobId(extractedJobOpeningId || responseData.id || jobPosting.id);
        setJobPostId(responseData.id || jobPosting.id);
        setJobPostData({
          ...jobPosting,
          documentRequirements,
          documentConfigurations,
        });
        setTemplate(jobPosting.htmlTemplate);

        if (jobPosting.id) {
          setJobPostUrl(`${window.location.origin}/jobs/${jobPosting.id}`);
        }
        if (
          jobPosting.videoFilename &&
          typeof jobPosting.videoFilename === 'string'
        ) {
          try {
            if (isHttpUrl(jobPosting.videoFilename)) {
              setUrl(jobPosting.videoFilename);
            } else {
              const videoUrl = await getVideoFromStorage(
                jobPosting.videoFilename,
              );
              setUrl(videoUrl);
            }
          } catch (err) {
            console.error('Error fetching video from storage:', err);
          }
        }
      } catch (error) {
        console.error('Error fetching job post:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchJobPost();
    }
  }, [id, contentLang]);

  // When job data loads or the UI language changes, switch content to match
  // the UI locale if a translation exists for it.
  useEffect(() => {
    const available = jobPostData?.availableLanguages ?? [];
    if (available.length === 0) return;
    const uiLang = i18n.language?.split('-')[0] ?? 'en';
    const target = uiLang === 'en' ? 'en' : available.includes(uiLang) ? uiLang : 'en';
    if (target !== contentLang) {
      setContentLang(target);
      // Keep i18n locale consistent with the resolved content language
      if (SUPPORTED_LOCALES.includes(target as SupportedLocale)) {
        setAppLocale(target as SupportedLocale);
      }
    }
  }, [jobPostData?.id, i18n.language]); // intentionally omit contentLang to avoid loop

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleEnded = () => setIsPlaying(false);

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('ended', handleEnded);

      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('ended', handleEnded);
      };
    }
  }, [url]);

  const handleSubmit = async () => {
    const docsToSubmit = Object.entries(selectedFiles).flatMap(([key, files]) =>
      files.map((file) => ({ file, type: key })),
    );

    if (docsToSubmit.length > 0) {
      setLoading(true);
      try {
        const response = await applyJob(jobPostId, docsToSubmit);
        if (response.ok) {
          let responseData: any = {};
          try {
            responseData = await response.json();
          } catch (e) {
            console.warn('Could not parse response JSON:', e);
          }

          // Capture Applicant ID for Pre-Qual submission
          const applicantId = responseData.job_applicant_id || responseData.id;
          if (applicantId) {
            setJobApplicantId(applicantId);
          }

          setIsModalOpen(false);
          setSelectedFiles({});

          // Force fetch latest questions immediately after upload using the Applicant ID (token)
          // This ensures the dialog opens for public users even if they don't have recruiter auth
          let freshQuestions: any[] = [];
          try {
            if (jobId) {
              const result = (await triggerPreQualForm(jobId)) as any;
              freshQuestions = result?.data?.questions || [];
            }

            // Fallback to jobId if no applicantId or no questions found (mainly for internal/recruiter testing)
            if (freshQuestions.length === 0 && jobId) {
              const result = (await triggerPreQual(jobId)) as any;
              freshQuestions = result?.data?.questions || [];
            }
          } catch (err) {
            console.error('Error fetching questions after upload:', err);
          }

          // Switch to Pre-Qualifying Questions if they exist in the fresh data
          if (freshQuestions.length > 0) {
            setIsQualifyingModalOpen(true);
            setCurrentQualifyingStep(0);
            toast.success(
              'Resume uploaded! Please answer these quick questions.',
              {
                position: 'bottom-center',
              },
            );
          } else {
            toast.success('Your application has been submitted!', {
              position: 'bottom-center',
            });
          }
        } else {
          let errorData: any = {};
          try {
            errorData = await response.json();
          } catch (e) {
            console.warn('Could not parse error response JSON:', e);
          }
          toast.error('Upload unsuccessful. Please try again.', {
            position: 'bottom-center',
          });
        }
      } catch (error) {
        console.error('Submission error:', error);
        toast.error('Error. Please try again.', {
          position: 'bottom-center',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFiles({});
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<DIALOG_TYPE>();

  const openDialog = (type: DIALOG_TYPE) => {
    setDialogType(type);
    setIsDialogOpen(true);
  };

  const handleApply = () => {
    setIsDialogOpen(false);
    setIsModalOpen(true); // Open document upload directly after privacy
  };

  const handleQualifyingComplete = async () => {
    if (currentQualifyingStep < qualifyingQuestions.length - 1) {
      setCurrentQualifyingStep((prev) => prev + 1);
    } else {
      // Submit Pre-Qual Answers
      if (jobApplicantId) {
        setLoading(true);
        try {
          const formattedAnswers = await Promise.all(
            qualifyingAnswers.map(async (ans) => {
              const videoFile = (ans as any).videoFile as File | undefined;
              let videoKey: string | undefined;

              if (videoFile) {
                const presign = await getVideoPresign({
                  questionKey: ans.questionId,
                  jobApplicantId,
                }).unwrap();
                const typelessBlob = new Blob([videoFile]);
                await fetch(presign.url, {
                  method: 'PUT',
                  body: typelessBlob,
                });
                videoKey = presign.videoKey || presign.url;
              }

              return {
                questionKey: ans.questionId,
                value: videoKey ? '' : String(ans.answer ?? ''),
                videoKey,
                location: (ans as any).locationData
                  ? {
                    city: (ans as any).locationData.city,
                    state: (ans as any).locationData.state,
                    country: (ans as any).locationData.country,
                    postcode: (ans as any).locationData.postcode,
                    lat: (ans as any).locationData.coordinates?.coordinates[1] || 0,
                    lng: (ans as any).locationData.coordinates?.coordinates[0] || 0,
                    unknown: false,
                  }
                  : {
                    city: '',
                    state: '',
                    country: '',
                    postcode: '',
                    lat: 0,
                    lng: 0,
                    unknown: true,
                  },
              };
            }),
          );

          await submitPublicPreQual({
            jobApplicantId,
            answers: formattedAnswers,
          }).unwrap();

          toast.success('Application complete! Thank you.', {
            position: 'bottom-center',
          });
        } catch (error) {
          console.error('Error submitting pre-qual:', error);
          toast.error(
            'Failed to submit supplementary info, but your resume was received.',
            {
              position: 'bottom-center',
            },
          );
        } finally {
          setLoading(false);
        }
      }
      setIsQualifyingModalOpen(false);
    }
  };

  // Returns whether a document type allows multiple file uploads
  const getAllowMultiple = (type: string): boolean => {
    if (jobPostData?.documentRequirements) {
      const normalizedType = type;
      const req = jobPostData.documentRequirements.find(
        (r) => (r.key || '') === normalizedType,
      );
      return req?.allowMultiple ?? false;
    }
    return false;
  };

  // Get available document keys from either documentRequirements or documentConfigurations
  const getAvailableDocumentKeys = (): string[] => {
    const keys: string[] = [];

    if (
      jobPostData?.documentRequirements &&
      jobPostData.documentRequirements.length > 0
    ) {
      keys.push(
        ...jobPostData.documentRequirements
          .map((req) => req.key || '')
          .filter(Boolean),
      );
    }

    if (
      jobPostData?.documentConfigurations &&
      jobPostData.documentConfigurations.length > 0
    ) {
      keys.push(
        ...jobPostData.documentConfigurations.map((config) => config.type),
      );
    }

    // Remove duplicates using filter
    return keys.filter((key, index, array) => array.indexOf(key) === index);
  };

  const handleFileSelect = (typeOrKey: string, file: File | null) => {
    setSelectedFiles((prev) => {
      const newFiles = { ...prev };
      if (file) {
        const canMultiple = getAllowMultiple(typeOrKey);
        if (canMultiple) {
          newFiles[typeOrKey] = [...(prev[typeOrKey] || []), file];
        } else {
          newFiles[typeOrKey] = [file]; // replace
        }
      } else {
        delete newFiles[typeOrKey];
      }
      return newFiles;
    });
  };

  const handleFileRemoveAtIndex = (type: string, index: number) => {
    setSelectedFiles((prev) => {
      const existing = prev[type] || [];
      const updated = existing.filter((_, i) => i !== index);
      if (updated.length === 0) {
        const { [type]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [type]: updated };
    });
  };

  const handleBatchFileSelect = (updates: { [type: string]: File | null }) => {
    setSelectedFiles((prev) => {
      const newFiles = { ...prev };
      Object.entries(updates).forEach(([type, file]) => {
        if (file) {
          const canMultiple = getAllowMultiple(type);
          if (canMultiple) {
            newFiles[type] = [...(prev[type] || []), file];
          } else {
            newFiles[type] = [file];
          }
        } else {
          delete newFiles[type];
        }
      });
      return newFiles;
    });
  };

  const formatUnixTimestamp = (timestamp: number | string): string => {
    // Handle null, undefined, empty string, or 0
    if (!timestamp) return 'TBD';

    const numericTimestamp =
      typeof timestamp === 'string' ? Number(timestamp) : timestamp;

    // If not a valid number or is 0, return TBD
    if (isNaN(numericTimestamp) || numericTimestamp === 0) {
      return 'TBD';
    }

    try {
      const date = new Date(numericTimestamp * 1000);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'TBD';
      }
      return date.toISOString().split('T')[0];
    } catch (error) {
      return 'TBD';
    }
  };

  const calculateDaysLeft = (): string => {
    const endTimestamp =
      typeof jobPostData?.endDate === 'string'
        ? Number(jobPostData?.endDate)
        : jobPostData?.endDate;

    if (!endTimestamp || endTimestamp <= 0 || isNaN(endTimestamp)) {
      return 'TBD';
    }

    try {
      const endDate = new Date(endTimestamp * 1000);
      const currentDate = new Date();

      if (isNaN(endDate.getTime())) {
        return 'TBD';
      }

      const timeDiff = endDate.getTime() - currentDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

      if (daysDiff < 0) {
        return 'Expired';
      } else if (daysDiff === 0) {
        return 'Expires today';
      } else if (daysDiff === 1) {
        return '1 day left';
      } else {
        return `${daysDiff} days left`;
      }
    } catch (error) {
      console.error('Error calculating days left:', error);
      return 'TBD';
    }
  };

  // Social sharing helper functions
  const getShareText = () => {
    const jobCompanyName = companyName || 'Company';
    const jobTitle = jobPostData?.jobTitle || 'Job Opportunity';
    return `Check out this job: ${jobTitle} at ${jobCompanyName}\n\n🔗 Apply here: ${jobPostUrl}`;
  };

  const getEmailSubject = () => {
    const jobCompanyName = companyName || 'Company';
    const jobTitle = jobPostData?.jobTitle || 'Job Opportunity';
    return `Job Opportunity: ${jobTitle} at ${jobCompanyName}`;
  };

  const getEmailBody = () => {
    const jobCompanyName = companyName || 'Company';
    const jobTitle = jobPostData?.jobTitle || 'Job Opportunity';
    return `Hey,\n\nI found this job opportunity and thought of you:\n\n📋 Position: ${jobTitle}\n🏢 Company: ${jobCompanyName}\n🔗 Apply here: ${jobPostUrl}\n\nThis looks like it could be a great fit for your skills!\n\nBest regards`;
  };

  // Generate mailto URL
  const getMailtoUrl = () => {
    if (!jobPostId || !jobPostUrl) return '';
    const subject = encodeURIComponent(getEmailSubject());
    const body = encodeURIComponent(getEmailBody());
    return `mailto:?subject=${subject}&body=${body}`;
  };

  // Social media sharing functions
  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(getShareText());
    const url = `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
    toast.success('WhatsApp opened!', { position: 'bottom-center' });
  };

  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      jobPostUrl,
    )}&quote=${encodeURIComponent(getShareText())}`;
    window.open(url, '_blank');
    toast.success('Facebook opened!', { position: 'bottom-center' });
  };

  const shareOnLinkedIn = () => {
    const title = encodeURIComponent(getEmailSubject());
    const summary = encodeURIComponent(
      `${getShareText().substring(0, 200)}...`,
    );
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      jobPostUrl,
    )}&title=${title}&summary=${summary}`;
    window.open(url, '_blank');
    toast.success('LinkedIn opened!', { position: 'bottom-center' });
  };

  const shareOnX = () => {
    const text = encodeURIComponent(`${getShareText().substring(0, 240)}...`);
    const url = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(
      jobPostUrl,
    )}`;
    window.open(url, '_blank');
    toast.success('X (Twitter) opened!', { position: 'bottom-center' });
  };

  const copyToClipboard = async () => {
    try {
      if (!jobPostUrl) {
        toast.error('Job post URL not available', {
          position: 'bottom-center',
        });
        return;
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(jobPostUrl);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = jobPostUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      toast.success('Job URL copied to clipboard!', {
        position: 'bottom-center',
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard', {
        position: 'bottom-center',
      });
    }
  };

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo.companyName);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const isMandatoryMissing = React.useMemo(() => {
    if (!jobPostData) return true;

    if (
      jobPostData.documentRequirements &&
      jobPostData.documentRequirements.length > 0
    ) {
      return jobPostData.documentRequirements.some(
        (req) => req.isMandatory && !(selectedFiles[req.key]?.length > 0),
      );
    }

    if (
      jobPostData.documentConfigurations &&
      jobPostData.documentConfigurations.length > 0
    ) {
      return jobPostData.documentConfigurations.some(
        (conf) =>
          conf.requirement === 'Mandatory' &&
          !(selectedFiles[conf.type]?.length > 0),
      );
    }

    return Object.keys(selectedFiles).length === 0;
  }, [jobPostData, selectedFiles]);

  return {
    jobPostData,
    loading,
    isDialogOpen,
    isModalOpen,
    selectedFiles,
    setSelectedFiles,
    url,
    isPlaying,
    videoRef,
    handlePlayPause,
    openDialog,
    handleApply,
    handleFileSelect,
    handleFileRemoveAtIndex,
    handleBatchFileSelect,
    handleSubmit,
    handleCloseModal,
    copyToClipboard,
    shareOnLinkedIn,
    shareOnWhatsApp,
    shareOnFacebook,
    shareOnX,
    formatUnixTimestamp,
    calculateDaysLeft,
    companyLogo,
    DIALOG_TYPE,
    mailtoUrl: getMailtoUrl(),
    setIsDialogOpen,
    isQualifyingModalOpen,
    setIsQualifyingModalOpen,
    isQualifyingCancelDialogOpen,
    setIsQualifyingCancelDialogOpen,
    currentQualifyingStep,
    setCurrentQualifyingStep,
    qualifyingQuestions,
    qualifyingAnswers,
    setQualifyingAnswers,
    handleQualifyingComplete,
    isFinalQualifyingStep:
      currentQualifyingStep === qualifyingQuestions.length - 1,
    canSubmit: !isMandatoryMissing && !loading,
    getAvailableDocumentKeys,
    ccaEnabled,
    companyName,
    contentLang,
    setContentLang: (lang: string) => {
      setContentLang(lang);
      // Also update the UI locale so i18n labels (Departemen, Lokasi, etc.)
      // switch to match the content language the user selected.
      if (SUPPORTED_LOCALES.includes(lang as SupportedLocale)) {
        setAppLocale(lang as SupportedLocale);
      }
    },
  };
};
